import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Option } from "app/types";
import React, { useCallback, useRef } from "react";
import { CheckIcon } from "../Icons";
import SearchIcon from "../Icons/SearchIcon";
import Input from "../Input";
import clsx from "clsx";
import CircularLoader from "../CircularLoader";


export interface SelectMenuItemProps<T> {
  options: T;
  onChange: (value: string) => void;
  setOpen: (boolean) => void;
  dropdownWidth: number;
  showEndIcon?: boolean;
  value: string;
  onLoadMore?: () => void; // function to load next page
  hasMore?: boolean; // whether more pages exist
  loading?: boolean; // loading state
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  searchPlaceholder:string
}

const DropdownMenuContentComponent: React.FC<SelectMenuItemProps<Option[]>> = ({
  options,
  onChange,
  setOpen,
  dropdownWidth,
  showEndIcon,
  value,
  hasMore,
  loading,
  onLoadMore,
  search,
  setSearch,
  searchPlaceholder
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    // When user is near the bottom, request more
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onLoadMore?.();
    }
  }, [loading, hasMore, onLoadMore]);

  return (
    <>
      <DropdownMenu.Content
        align="start"
        sideOffset={5}
        side="bottom"
        style={{ width: dropdownWidth }}
        className={`bg-white shadow-lg rounded-md z-50 w-64 border border-[#E9ECF2]  py-2 `}
      >
         <div className="">
            <Input
              noBorder
              id="search"
              icon={<SearchIcon size="17" color="#79808A" />}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              className=""
              innerContainerClassName="px-2 border-b border-gray-100 "
            />
          </div>
        <div
          className={clsx("overflow-auto max-h-[270px] ")}
          onScroll={handleScroll}
          ref={scrollRef}
        >
          {options.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={`cursor-pointer hover:outline-none text-sm leading-[21px] font-normal text-secondary hover:bg-[#F4F5F7] px-[8px] pl-[16px] py-[10px] flex items-center gap-2  overflow-y-auto `}
            >
              <div className="flex flex-1 items-start gap-2">
                {option.icon && (
                  <div className="min-w-[20px] min-h-[20px]">{option.icon}</div>
                )}
                <div className="flex flex-col">
                  <span
                    className={`text-sm leading-[21px] font-normal text-black `}
                  >
                    {option.label}
                  </span>
                </div>
              </div>
              {showEndIcon && value === option.value && (
                <CheckIcon color="#5A8DF1" />
              )}
            </DropdownMenu.Item>
          ))}
          {/* Loader or "No more data" */}
          {loading && (
            <div className="py-2 text-center text-xs text-gray-400">
              <CircularLoader size="sm" />
            </div>
          )}
          {!hasMore && !loading && (
            <div className="py-2 text-center text-xs text-gray-300  ">
              No more options
            </div>
          )}
        </div>
      </DropdownMenu.Content>
    </>
  );
};

export default DropdownMenuContentComponent;
